import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Calendar, 
  MapPin, 
  Tag, 
  CheckCircle, 
  Smartphone, 
  Edit3, 
  Trash2, 
  Eye,
  Share2,
  FolderPlus,
  Layers,
  Send,
  RotateCcw,
  Sparkles,
  CheckCircle2,
  SlidersHorizontal,
  Upload,
  Crop as CropIcon,
  Image as ImageIcon,
  Megaphone,
  Palette,
  Globe,
  Layout
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { ImageCropperModal } from '../components/common/ImageCropperModal';
import { CMSArticle, PostCategory } from '../types/admin';
import { CategoryShowcaseManager } from '../components/cms/CategoryShowcaseManager';
import { BannerCarouselManager } from '../components/cms/BannerCarouselManager';
import { FooterSettingsManager } from '../components/cms/FooterSettingsManager';
import { DynamicPagesManager } from '../components/cms/DynamicPagesManager';
import { LandingPageLayoutArranger } from '../components/cms/LandingPageLayoutArranger';
import { AdminApiClient } from '../services/adminApiClient';

export const CMSView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'published' | 'drafts' | 'categories' | 'banners' | 'footer' | 'pages' | 'layout'>('published');

  // Categories state
  const [categories, setCategories] = useState<PostCategory[]>([
    {
      id: 'cat_01',
      name: 'Sustainability Blog',
      slug: 'blog',
      description: 'Educational articles on eco-living, circular economy tips, and thrift lifestyle.',
      color: '#0df2a6',
      articleCount: 1,
    },
    {
      id: 'cat_02',
      name: 'Community Events',
      slug: 'event',
      description: 'Announcements for local flea markets, swap meets, and zero-waste fairs across Nigeria.',
      color: '#38bdf8',
      articleCount: 1,
    },
    {
      id: 'cat_03',
      name: 'Platform News',
      slug: 'news',
      description: 'Official releases, app feature updates, and escrow service policies.',
      color: '#fbbf24',
      articleCount: 1,
    },
    {
      id: 'cat_04',
      name: 'Zero Waste Lagos',
      slug: 'zero-waste-lagos',
      description: 'Spotlights on local initiatives preventing landfill overflow in Lagos State.',
      color: '#a855f7',
      articleCount: 0,
    },
    {
      id: 'cat_05',
      name: 'Tech Tips & Gadget Care',
      slug: 'tech-tips',
      description: 'Hardware refurbishing advice, battery health preservation, and phone testing guides.',
      color: '#f43f5e',
      articleCount: 0,
    }
  ]);

  // Articles state (published & drafts)
  const [articles, setArticles] = useState<CMSArticle[]>([
    {
      id: 'cms_art_001',
      title: 'Lagos Green Thrift Fair 2026: Trade, Swap & Divert Carbon',
      slug: 'lagos-green-thrift-fair-2026',
      category: 'event',
      summary: 'Join over 2,000 conscious consumers in Victoria Island for the largest second-hand electronics and fashion swap festival in West Africa.',
      content: 'The ReUseIt team is thrilled to host the 2026 Lagos Green Thrift Fair. Bring pre-loved gadgets and clothing to exchange, verify items on-site via our mobile app escrow desk, and calculate your lifetime household carbon diversion score!',
      coverImage: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&auto=format&fit=crop&q=80',
      tags: ['EcoFest', 'LagosEvents', 'CircularEconomy'],
      status: 'published',
      author: 'ReUseIt Editorial Team',
      eventDate: 'Saturday, Oct 10, 2026 • 10:00 AM',
      eventLocation: 'Muri Okunola Park, Victoria Island, Lagos',
      publishedAt: 'Yesterday'
    },
    {
      id: 'cms_art_002',
      title: 'E-Waste Drive: How to Responsibly Retire Your Lithium Batteries',
      slug: 'e-waste-drive-lithium-batteries',
      category: 'news',
      summary: 'ReUseIt partners with certified recycling facilities across Ikeja and Abuja to prevent heavy metal contamination in domestic landfills.',
      content: 'Old smartphones and laptop batteries release harmful electrolytes if discarded in municipal waste. Discover our 12 drop-off hubs across Lagos and earn Eco-Points for every verified battery handed in.',
      coverImage: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?w=800&auto=format&fit=crop&q=80',
      tags: ['EWaste', 'BatteryRecycling', 'CleanNigeria'],
      status: 'published',
      author: 'Sustainability Desk',
      publishedAt: '3 days ago'
    },
    {
      id: 'cms_art_003',
      title: '5 Ways Nigerian Students Are Funding Their Semester Through Thrift',
      slug: 'students-funding-semester-thrift',
      category: 'blog',
      summary: 'From UNILAG to University of Ibadan, campus thrift vendors share their top tactics for buying, refurbishing, and flipping pre-owned gear.',
      content: 'College campuses are bustling micro-economies. Learn how students utilize ReUseIt 48-hour inspection protection to safely trade textbooks, monitors, and laptops without getting scammed.',
      coverImage: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&auto=format&fit=crop&q=80',
      tags: ['CampusEconomy', 'StudentHustle', 'SmartLiving'],
      status: 'draft',
      author: 'Campus Ambassador Lead',
      publishedAt: null
    },
    {
      id: 'cms_art_004',
      title: 'Draft: Ultimate Guide to Verifying Pre-Owned iPhones in Nigeria',
      slug: 'guide-verifying-used-iphones',
      category: 'blog',
      summary: 'Check 3uTools, IMEI blacklist status, battery cycle count, and true tone authenticity before accepting delivery.',
      content: 'A comprehensive checklist for Nigerian buyers when testing phones during the ReUseIt 48-hour escrow inspection period.',
      coverImage: 'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=800&auto=format&fit=crop&q=80',
      tags: ['iPhoneCheck', 'BuyerProtection', 'TechSafety'],
      status: 'draft',
      author: 'Hardware Moderator',
      publishedAt: null
    }
  ]);

  useEffect(() => {
    AdminApiClient.getArticles()
      .then((data) => {
        if (data && data.length > 0) {
          const mapped: CMSArticle[] = data.map((a: any) => ({
            id: a.id,
            title: a.title,
            slug: a.slug,
            category: (a.category as any) || 'blog',
            summary: a.content?.slice(0, 160) || '',
            content: a.content || '',
            coverImage: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&auto=format&fit=crop&q=80',
            tags: ['ReUseIt', a.category],
            status: a.isPublished ? 'published' : 'draft',
            author: a.authorName || 'Super Admin',
            publishedAt: new Date(a.updatedAt).toLocaleDateString(),
            createdAt: a.createdAt,
          }));
          setArticles(mapped);
        }
      })
      .catch((err) => console.error('Failed to load CMS articles:', err));
  }, []);

  // Article Modal State (Create / Edit)
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<CMSArticle>>({
    title: '',
    category: 'blog',
    summary: '',
    content: '',
    coverImage: '',
    author: 'ReUseIt Admin',
    status: 'published',
    tags: ['ReUseIt']
  });

  // Mobile frame simulator preview
  const [previewArticle, setPreviewArticle] = useState<CMSArticle | null>(null);

  // Cover Image Cropper & Optimization State (16:9 Widescreen)
  const [cmsCropperOpen, setCmsCropperOpen] = useState(false);
  const [rawCoverImageSrc, setRawCoverImageSrc] = useState('');
  const [coverCropStats, setCoverCropStats] = useState<{ originalSizeKb: number; optimizedSizeKb: number } | null>(null);

  const handleCoverFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawCoverImageSrc(reader.result as string);
      setCmsCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCoverCropComplete = (croppedDataUrl: string, stats: { originalSizeKb: number; optimizedSizeKb: number }) => {
    setFormData(prev => ({ ...prev, coverImage: croppedDataUrl }));
    setCoverCropStats(stats);
  };

  // Category Modal State (Add / Edit)
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PostCategory | null>(null);
  const [catName, setCatName] = useState('');
  const [catSlug, setCatSlug] = useState('');
  const [catDesc, setCatDesc] = useState('');
  const [catColor, setCatColor] = useState('#0df2a6');

  // Deletion confirmation
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'article' | 'category'; name: string } | null>(null);

  // Filtered views
  const publishedArticles = articles.filter(a => a.status === 'published');
  const draftedArticles = articles.filter(a => a.status === 'draft');

  // 1. OPEN CREATE ARTICLE MODAL
  const handleOpenCreateArticle = (defaultStatus: 'published' | 'draft' = 'published') => {
    setEditingArticleId(null);
    setCoverCropStats(null);
    setFormData({
      title: '',
      category: categories[0]?.slug || 'blog',
      summary: '',
      content: '',
      coverImage: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&auto=format&fit=crop&q=80',
      author: 'Super Admin',
      status: defaultStatus,
      tags: ['Thrift', 'Nigeria', 'EcoLiving']
    });
    setEditorModalOpen(true);
  };

  // 2. OPEN EDIT ARTICLE MODAL (FOR PUBLISHED OR DRAFT)
  const handleOpenEditArticle = (art: CMSArticle) => {
    setEditingArticleId(art.id);
    setCoverCropStats(null);
    setFormData({
      title: art.title,
      slug: art.slug,
      category: art.category,
      summary: art.summary,
      content: art.content,
      coverImage: art.coverImage,
      author: art.author,
      status: art.status,
      tags: [...art.tags],
      eventDate: art.eventDate,
      eventLocation: art.eventLocation
    });
    setEditorModalOpen(true);
  };

  // 3. SAVE ARTICLE (CREATE OR UPDATE)
  const handleSaveArticle = () => {
    if (!formData.title?.trim()) return;

    const slug = (formData.title || '').toLowerCase().replace(/\s+/g, '-');
    AdminApiClient.saveArticle({
      id: editingArticleId || undefined,
      slug,
      title: formData.title || '',
      category: (formData.category as string) || 'blog',
      content: formData.content || '',
      authorName: formData.author || 'Super Admin',
      isPublished: formData.status === 'published',
    }).catch(console.error);

    if (editingArticleId) {
      // Update existing post
      setArticles(prev => prev.map(a => {
        if (a.id === editingArticleId) {
          return {
            ...a,
            title: formData.title || a.title,
            slug: (formData.title || a.title).toLowerCase().replace(/\s+/g, '-'),
            category: (formData.category as any) || a.category,
            summary: formData.summary || a.summary,
            content: formData.content || a.content,
            coverImage: formData.coverImage || a.coverImage,
            tags: formData.tags || a.tags,
            status: formData.status || a.status,
            eventDate: formData.eventDate,
            eventLocation: formData.eventLocation,
            publishedAt: formData.status === 'published' ? (a.publishedAt || 'Just now') : null
          };
        }
        return a;
      }));
    } else {
      // Create new post
      const newArt: CMSArticle = {
        id: `cms_art_${Date.now()}`,
        title: formData.title,
        slug: formData.title.toLowerCase().replace(/\s+/g, '-'),
        category: (formData.category as any) || 'blog',
        summary: formData.summary || '',
        content: formData.content || '',
        coverImage: formData.coverImage || 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&auto=format&fit=crop&q=80',
        tags: formData.tags || ['ReUseIt'],
        status: formData.status || 'published',
        author: formData.author || 'Super Admin',
        eventDate: formData.eventDate,
        eventLocation: formData.eventLocation,
        publishedAt: formData.status === 'published' ? 'Just now' : null,
        createdAt: new Date().toISOString()
      };
      setArticles([newArt, ...articles]);
    }

    setEditorModalOpen(false);
  };

  // 4. INSTANT PUBLISH DRAFT
  const handleInstantPublish = (id: string) => {
    setArticles(prev => prev.map(a => {
      if (a.id === id) {
        return { ...a, status: 'published', publishedAt: 'Just now' };
      }
      return a;
    }));
  };

  // 5. UNPUBLISH (MOVE TO DRAFTS)
  const handleUnpublishToDraft = (id: string) => {
    setArticles(prev => prev.map(a => {
      if (a.id === id) {
        return { ...a, status: 'draft', publishedAt: null };
      }
      return a;
    }));
  };

  // 6. CATEGORY MANAGEMENT: ADD / EDIT
  const handleOpenAddCategory = () => {
    setEditingCategory(null);
    setCatName('');
    setCatSlug('');
    setCatDesc('');
    setCatColor('#0df2a6');
    setCategoryModalOpen(true);
  };

  const handleOpenEditCategory = (cat: PostCategory) => {
    setEditingCategory(cat);
    setCatName(cat.name);
    setCatSlug(cat.slug);
    setCatDesc(cat.description);
    setCatColor(cat.color);
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = () => {
    if (!catName.trim()) return;
    const computedSlug = catSlug.trim() || catName.toLowerCase().replace(/\s+/g, '-');

    if (editingCategory) {
      setCategories(prev => prev.map(c => {
        if (c.id === editingCategory.id) {
          return {
            ...c,
            name: catName,
            slug: computedSlug,
            description: catDesc,
            color: catColor
          };
        }
        return c;
      }));
    } else {
      const newCat: PostCategory = {
        id: `cat_${Date.now()}`,
        name: catName,
        slug: computedSlug,
        description: catDesc,
        color: catColor,
        articleCount: 0
      };
      setCategories([...categories, newCat]);
    }

    setCategoryModalOpen(false);
  };

  // 7. CONFIRM DELETION
  const handleExecuteDelete = () => {
    if (!itemToDelete) return;
    if (itemToDelete.type === 'article') {
      AdminApiClient.deleteArticle(itemToDelete.id).catch(console.error);
      setArticles(prev => prev.filter(a => a.id !== itemToDelete.id));
    } else {
      setCategories(prev => prev.filter(c => c.id !== itemToDelete.id));
    }
    setItemToDelete(null);
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Tab Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] pb-4">
        <div>
          <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
            Content Management System (CMS) & Dynamic Pages
          </h3>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
            Manage dynamic web pages, legal policies, ad banners, footer links, and news articles
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAddCategory}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-[#90cbb7] hover:text-white bg-[#152a23] hover:bg-[#22493c] border border-[#22493c] transition-all"
          >
            <FolderPlus className="w-4 h-4 text-brand-400" />
            <span>New Category</span>
          </button>

          <button
            onClick={() => handleOpenCreateArticle('published')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Create Article / Event</span>
          </button>
        </div>
      </div>

      {/* 5-TAB NAVIGATOR */}
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          onClick={() => setActiveTab('published')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'published'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span>Published Articles ({publishedArticles.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('drafts')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'drafts'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Edit3 className="w-3.5 h-3.5" />
          <span>Drafted Posts ({draftedArticles.length})</span>
          {draftedArticles.length > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${activeTab === 'drafts' ? 'bg-forest-void/20 text-forest-void' : 'bg-amber-500 text-forest-void'}`}>
              {draftedArticles.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('banners')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'banners'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Megaphone className="w-3.5 h-3.5" />
          <span>Ad Banners & Carousels</span>
        </button>

        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'categories'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Palette className="w-3.5 h-3.5" />
          <span>Category Showcase & Colors</span>
        </button>

        <button
          onClick={() => setActiveTab('footer')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'footer'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Globe className="w-3.5 h-3.5" />
          <span>Web Footer Manager</span>
        </button>

        <button
          onClick={() => setActiveTab('pages')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'pages'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <FileText className="w-3.5 h-3.5" />
          <span>Dynamic Pages & Policies</span>
        </button>

        <button
          onClick={() => setActiveTab('layout')}
          className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2
            ${activeTab === 'layout'
              ? 'bg-brand-500 text-forest-void shadow-glow-sm'
              : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
            }
          `}
        >
          <Layout className="w-3.5 h-3.5" />
          <span>Landing Page Layout</span>
        </button>
      </div>

      {/* TAB 1: PUBLISHED ARTICLES */}
      {activeTab === 'published' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {publishedArticles.map((art) => (
            <div 
              key={art.id} 
              className="rounded-3xl border overflow-hidden transition-all flex flex-col justify-between
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/30
                light:bg-white light:border-[#D5E4DC] light:shadow-sm
              "
            >
              <div>
                {/* Cover Image Banner */}
                <div className="relative h-44 w-full overflow-hidden bg-[#10231d]">
                  <img src={art.coverImage} alt={art.title} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-brand-500 text-forest-void shadow-glow-sm">
                      {art.category}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      Live
                    </span>
                  </div>
                </div>

                {/* Body Content */}
                <div className="p-5 space-y-3 text-xs">
                  <h4 className="text-sm font-bold text-white dark:text-white light:text-[#0D1C17] line-clamp-2">
                    {art.title}
                  </h4>

                  <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] line-clamp-2 leading-relaxed">
                    {art.summary}
                  </p>

                  {art.eventDate && (
                    <div className="flex items-center gap-1.5 text-[11px] text-amber-400 font-semibold">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{art.eventDate}</span>
                    </div>
                  )}

                  {art.eventLocation && (
                    <div className="flex items-center gap-1.5 text-[11px] text-[#90cbb7] light:text-gray-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{art.eventLocation}</span>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {art.tags.map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-full text-[10px] bg-[#10231d] dark:bg-[#10231d] light:bg-gray-100 text-[#90cbb7] light:text-gray-600">
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions Footer */}
              <div className="p-4 bg-[#10231d]/60 dark:bg-[#10231d]/60 light:bg-gray-50 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] flex items-center justify-between">
                <span className="text-[11px] text-[#90cbb7] light:text-gray-500">By {art.author}</span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenEditArticle(art)}
                    className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-500/15 transition-colors"
                    title="Edit Published Article"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setPreviewArticle(art);
                    }}
                    className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] light:hover:bg-gray-200"
                    title="Mobile App Frame Preview"
                  >
                    <Smartphone className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleUnpublishToDraft(art.id)}
                    className="p-1.5 rounded-lg text-amber-400 hover:bg-amber-500/15"
                    title="Move back to Drafts"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setItemToDelete({ id: art.id, type: 'article', name: art.title })}
                    className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15"
                    title="Delete Post"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 2: DRAFTED POSTS QUEUE */}
      {activeTab === 'drafts' && (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Edit3 className="w-4 h-4 shrink-0" />
              <span>
                <strong>Draft Queue:</strong> These posts are hidden from mobile app users until published. You can revise content, update categories, or publish immediately.
              </span>
            </div>
            <button
              onClick={() => handleOpenCreateArticle('draft')}
              className="px-3 py-1.5 rounded-full text-xs font-bold text-forest-void bg-amber-400 hover:bg-amber-300 shrink-0"
            >
              + New Draft
            </button>
          </div>

          {draftedArticles.length === 0 ? (
            <div className="p-12 text-center rounded-3xl border border-[#22493c] bg-[#152a23] text-[#90cbb7]">
              <p className="text-sm">No drafted articles currently in the queue.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draftedArticles.map((art) => (
                <div 
                  key={art.id} 
                  className="rounded-3xl border overflow-hidden transition-all flex flex-col justify-between
                    bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-amber-500/40
                    light:bg-white light:border-[#D5E4DC] light:shadow-sm
                  "
                >
                  <div>
                    {/* Image */}
                    <div className="relative h-40 w-full overflow-hidden bg-[#10231d]">
                      <img src={art.coverImage} alt={art.title} className="w-full h-full object-cover opacity-80" />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-500 text-forest-void">
                          Draft
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-black/60 text-white backdrop-blur-sm">
                          {art.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-5 space-y-2 text-xs">
                      <h4 className="text-sm font-bold text-white dark:text-white light:text-[#0D1C17] line-clamp-2">
                        {art.title}
                      </h4>
                      <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] line-clamp-2">
                        {art.summary}
                      </p>
                      <div className="text-[11px] text-[#90cbb7]/70 pt-1">
                        Author: {art.author}
                      </div>
                    </div>
                  </div>

                  {/* Draft Actions */}
                  <div className="p-4 bg-[#10231d]/60 dark:bg-[#10231d]/60 light:bg-gray-50 border-t border-[#22493c] dark:border-[#22493c] flex items-center justify-between gap-2">
                    <button
                      onClick={() => handleInstantPublish(art.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
                    >
                      <Send className="w-3 h-3" />
                      <span>Publish Now</span>
                    </button>

                    <button
                      onClick={() => handleOpenEditArticle(art)}
                      className="p-2 rounded-xl text-white bg-[#22493c] hover:bg-[#2a5547] transition-colors"
                      title="Edit Draft Content"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => setItemToDelete({ id: art.id, type: 'article', name: art.title })}
                      className="p-2 rounded-xl text-rose-400 hover:bg-rose-500/15 transition-colors"
                      title="Delete Draft"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: AD BANNERS & HERO CAROUSELS MANAGER */}
      {activeTab === 'banners' && <BannerCarouselManager />}

      {/* TAB 4: CATEGORY SHOWCASE & COLOR PICKER (JUMIA MODEL) */}
      {activeTab === 'categories' && <CategoryShowcaseManager />}

      {/* TAB 5: WEB FOOTER & NAVIGATION SETTINGS */}
      {activeTab === 'footer' && <FooterSettingsManager />}

      {/* TAB 6: DYNAMIC PAGES & POLICIES BUILDER */}
      {activeTab === 'pages' && <DynamicPagesManager />}

      {/* TAB 7: LANDING PAGE LAYOUT BUILDER & ARRANGER */}
      {activeTab === 'layout' && <LandingPageLayoutArranger />}

      {/* ARTICLE EDITOR MODAL (CREATE OR EDIT) */}
      {editorModalOpen && (
        <Modal
          isOpen={editorModalOpen}
          onClose={() => setEditorModalOpen(false)}
          title={editingArticleId ? `Edit Article: ${formData.title}` : 'Compose New Article or Event'}
          subtitle={editingArticleId ? 'Changes will update live across the mobile app' : 'Content published here streams live to users'}
          maxWidth="2xl"
        >
          <div className="space-y-4 text-xs">
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Article Title:
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Lagos Eco-Fair: 5 Steps to Zero Waste"
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                  Category:
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.slug}>{c.name} (/{c.slug})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                  Publishing Status:
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
                >
                  <option value="published">Published (Live in App)</option>
                  <option value="draft">Save as Draft (Unpublished)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Cover Image Banner (16:9 Aspect Ratio):
              </label>

              <div className="p-3.5 rounded-2xl bg-[#10231d] dark:bg-[#10231d] border border-[#22493c] light:bg-[#F4F8F5] light:border-[#D5E4DC] space-y-3">
                {formData.coverImage && (
                  <div className="relative rounded-xl overflow-hidden aspect-video border border-[#22493c] bg-black/40">
                    <img
                      src={formData.coverImage}
                      alt="Cover Preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-white text-[10px] font-mono backdrop-blur-sm">
                      16:9 Banner
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2.5">
                  <label className="cursor-pointer px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void font-bold text-xs shadow-glow-sm transition-all flex items-center justify-center gap-2">
                    <Upload className="w-3.5 h-3.5 stroke-[2.5]" />
                    <span>Upload & Crop Banner (.jpg, .jpeg, .png)</span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      onChange={handleCoverFileSelect}
                      className="hidden"
                    />
                  </label>
                  <span className="text-[11px] text-[#90cbb7]/70">16:9 widescreen cropped & auto-compressed</span>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-[#90cbb7] light:text-[#527063]">
                    Or Specify Custom Image URL:
                  </label>
                  <input
                    type="text"
                    value={formData.coverImage}
                    onChange={(e) => {
                      setFormData({ ...formData, coverImage: e.target.value });
                      setCoverCropStats(null);
                    }}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full p-2.5 rounded-xl text-xs outline-none border transition-colors
                      bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                      light:bg-white light:border-[#D5E4DC] light:text-[#0D1C17]"
                  />
                </div>

                {coverCropStats && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-brand-500/10 border border-brand-500/25 text-[11px] text-brand-300">
                    <CropIcon className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                    <span>
                      16:9 Cropped & Optimized: <strong>{coverCropStats.originalSizeKb} KB</strong> → <strong>{coverCropStats.optimizedSizeKb} KB</strong> ({Math.max(0, Math.round((1 - coverCropStats.optimizedSizeKb / coverCropStats.originalSizeKb) * 100))}% reduction)
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                  Event Date (If applicable):
                </label>
                <input
                  type="text"
                  value={formData.eventDate || ''}
                  onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                  placeholder="e.g. Saturday, Oct 10 • 10:00 AM"
                  className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                  Event Location:
                </label>
                <input
                  type="text"
                  value={formData.eventLocation || ''}
                  onChange={(e) => setFormData({ ...formData, eventLocation: e.target.value })}
                  placeholder="e.g. Victoria Island, Lagos"
                  className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                    bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                    light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Summary / Teaser:
              </label>
              <input
                type="text"
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Brief 1-2 sentence preview..."
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Article Body:
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Write full article body..."
                rows={5}
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex justify-end gap-3">
              <button
                onClick={() => setEditorModalOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveArticle}
                disabled={!formData.title?.trim()}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50"
              >
                {editingArticleId ? 'Save Revisions' : 'Save & Publish'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* CATEGORY MODAL (ADD / EDIT) */}
      {categoryModalOpen && (
        <Modal
          isOpen={categoryModalOpen}
          onClose={() => setCategoryModalOpen(false)}
          title={editingCategory ? `Edit Category: ${editingCategory.name}` : 'Add New Category'}
          subtitle="Configure post taxonomy and visual badges for app navigation"
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Category Name:
              </label>
              <input
                type="text"
                value={catName}
                onChange={(e) => {
                  setCatName(e.target.value);
                  if (!editingCategory) {
                    setCatSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
                  }
                }}
                placeholder="e.g. Circular Economy"
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                URL Slug:
              </label>
              <input
                type="text"
                value={catSlug}
                onChange={(e) => setCatSlug(e.target.value)}
                placeholder="e.g. circular-economy"
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors font-mono
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Description:
              </label>
              <textarea
                value={catDesc}
                onChange={(e) => setCatDesc(e.target.value)}
                placeholder="Brief description of articles that belong here..."
                rows={2}
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Color Accent:
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={catColor}
                  onChange={(e) => setCatColor(e.target.value)}
                  className="w-10 h-10 rounded-xl bg-transparent cursor-pointer border border-[#22493c]"
                />
                <span className="font-mono text-xs text-white dark:text-white light:text-[#0D1C17]">{catColor}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex justify-end gap-3">
              <button
                onClick={() => setCategoryModalOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={!catName.trim()}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50"
              >
                {editingCategory ? 'Update Category' : 'Save Category'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* MOBILE FRAME PREVIEW MODAL */}
      {previewArticle && (
        <Modal
          isOpen={Boolean(previewArticle)}
          onClose={() => setPreviewArticle(null)}
          title="Mobile In-App Reader Preview"
          subtitle="Pixel-accurate simulation of the ReUseIt mobile smartphone reader"
          maxWidth="md"
        >
          <div className="flex justify-center py-2">
            <div className="w-[320px] rounded-[36px] p-4 bg-[#10231d] border-4 border-[#22493c] shadow-2xl space-y-3">
              <div className="w-24 h-4 bg-[#152a23] rounded-full mx-auto mb-2"></div>
              
              <div className="rounded-2xl overflow-hidden h-40">
                <img src={previewArticle.coverImage} alt={previewArticle.title} className="w-full h-full object-cover" />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-500 text-forest-void">
                  {previewArticle.category.toUpperCase()}
                </span>
                <span className="text-[10px] text-[#90cbb7]">
                  {previewArticle.publishedAt || 'Draft'}
                </span>
              </div>

              <h4 className="font-bold text-white text-sm leading-tight">
                {previewArticle.title}
              </h4>

              <p className="text-[11px] text-[#90cbb7] leading-relaxed">
                {previewArticle.content}
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* DELETION CONFIRMATION DIALOG */}
      {itemToDelete && (
        <ConfirmDialog
          isOpen={Boolean(itemToDelete)}
          onClose={() => setItemToDelete(null)}
          onConfirm={handleExecuteDelete}
          title={`Delete ${itemToDelete.type === 'article' ? 'Article' : 'Category'}: ${itemToDelete.name}?`}
          description={`Are you sure you want to permanently delete this ${itemToDelete.type}? This action will remove it from the system.`}
          confirmButtonText={`Delete ${itemToDelete.type}`}
          variant="danger"
        />
      )}

      {/* 16:9 Post Cover Cropper Modal */}
      {cmsCropperOpen && (
        <ImageCropperModal
          isOpen={cmsCropperOpen}
          onClose={() => setCmsCropperOpen(false)}
          imageSrc={rawCoverImageSrc}
          aspectRatio="16:9"
          title="Crop & Optimize Post Cover Banner (16:9)"
          onCropComplete={handleCoverCropComplete}
        />
      )}
    </div>
  );
};
